import { formatEnum } from '@/lib/format';

export const HELP_TICKET_CATEGORY_LABELS: Record<string, string> = {
  HOW_TO: 'How to',
  BUG_REPORT: 'Bug report',
  FEATURE_REQUEST: 'Feature request',
  DATA_ISSUE: 'Data issue',
  ACCESS_ISSUE: 'Access issue',
  PLATFORM_ISSUE: 'Platform issue',
  BILLING_ISSUE: 'Billing issue',
  OTHER: 'Other',
};

export const HELP_TICKET_PRIORITY_LABELS: Record<string, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
};

export const HELP_TICKET_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
};

export const HELP_TICKET_TIER_LABELS: Record<string, string> = {
  INTERNAL: 'Internal',
  STATURA: 'Statura',
};

export const HELP_TICKET_PRIORITY_VARIANTS: Record<
  string,
  'compliant' | 'warning' | 'non-compliant' | 'pending' | 'draft'
> = {
  LOW: 'compliant',
  MEDIUM: 'pending',
  HIGH: 'warning',
  CRITICAL: 'non-compliant',
};

export const HELP_TICKET_STATUS_VARIANTS: Record<
  string,
  'compliant' | 'warning' | 'non-compliant' | 'pending' | 'draft'
> = {
  OPEN: 'draft',
  IN_PROGRESS: 'pending',
  RESOLVED: 'compliant',
  CLOSED: 'compliant',
};

export const HELP_TICKET_TIER_VARIANTS: Record<
  string,
  'compliant' | 'warning' | 'non-compliant' | 'pending' | 'draft'
> = {
  INTERNAL: 'draft',
  STATURA: 'pending',
};

export const HELP_TICKET_CATEGORY_OPTIONS = Object.entries(HELP_TICKET_CATEGORY_LABELS).map(
  ([value, label]) => ({ value, label }),
);

export const HELP_TICKET_PRIORITY_OPTIONS = Object.entries(HELP_TICKET_PRIORITY_LABELS).map(
  ([value, label]) => ({ value, label }),
);

function labelFromMap(value: string | null | undefined, map: Record<string, string>): string {
  if (!value) return '—';
  return map[value] ?? formatEnum(value);
}

export function formatHelpTicketCategory(value: string | null | undefined): string {
  return labelFromMap(value, HELP_TICKET_CATEGORY_LABELS);
}

export function formatHelpTicketPriority(value: string | null | undefined): string {
  return labelFromMap(value, HELP_TICKET_PRIORITY_LABELS);
}

export function formatHelpTicketStatus(value: string | null | undefined): string {
  return labelFromMap(value, HELP_TICKET_STATUS_LABELS);
}

export function formatHelpTicketTier(value: string | null | undefined): string {
  return labelFromMap(value, HELP_TICKET_TIER_LABELS);
}

export const VISIBLE_HELP_TAG_LIMIT = 3;

export function visibleHelpTags(tags: readonly string[], limit = VISIBLE_HELP_TAG_LIMIT): string[] {
  return tags.filter((tag) => tag !== 'cross-link').slice(0, limit);
}

export function formatHelpTag(tag: string): string {
  return formatEnum(tag);
}

export function formatHelpCopy(text: string): string {
  return text.replace(/\b[A-Z][A-Z0-9]+(?:_[A-Z0-9]+)+\b/g, (token) => formatEnum(token));
}

export function formatHelpAuditChange(key: string, value: unknown): string {
  const field = formatEnum(key);
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const from = record.from;
    const to = record.to;
    if (from !== undefined || to !== undefined) {
      return `${field}: ${formatEnum(from == null ? null : String(from))} → ${formatEnum(to == null ? null : String(to))}`;
    }
    return `${field}: ${Object.entries(record)
      .map(([innerKey, innerValue]) => `${formatEnum(innerKey)} ${formatEnum(String(innerValue))}`)
      .join(', ')}`;
  }
  return `${field}: ${formatEnum(value == null ? null : String(value))}`;
}
